import type { PalmOnCompleteCallbackImage, PalmThresholds } from '@innovatrics/dot-palm-capture';
import type { PalmInstructions } from '@innovatrics/dot-auto-capture-ui/palm';

import { Spacer } from '@dot/design-system';
import { useTranslation } from 'react-i18next';

import { onAutoCaptureError } from '../utils/error';

import { PalmCamera } from './components/palm-camera';
import { PalmCameraUi } from './components/palm-camera-ui';
import * as SC from './palm-capture.styles';
import { PalmUiStep, useUploadPalm } from './hooks/use-upload-palm';

import { Loader } from '@/atoms/Loader/Loader';
import { ManualMessageUpload } from '@/components/ManualUpload/ManualMessageUpload';
import { SecuredByBadge } from '@/components/SecuredByBadge/SecuredByBadge';
import { CaptureWrapper } from '@/components/capture-wrapper';
import { config } from '@/config';
import * as GSC from '@/styles/GlobalStyles';
import { getPublicAsset } from '@/utils/utils';

/**
 * SAIDVU-499/501 — palm capture step. Renders the real DOT web palm auto-capture component for
 * both `both_palms_capture` (onboarding, forces both hands via the BFF other-palm instruction)
 * and `single_palm_capture` (authentication, either hand). One component for both: the BFF drives
 * the two-phase flow through `nextStep.data.palmCapture` (see `useUploadPalm`).
 */
/*
 * Client capture-quality thresholds for the DOT palm auto-capture. The size band is the important
 * one: the SDK's mobile-oriented default (~0.4 min) means a palm held at arm's length in front of a
 * laptop webcam never fills enough of the frame to satisfy it, so auto-capture never fires and the
 * user is stuck. Widening the lower bound to the documented desktop value (0.34) lets a palm at
 * laptop distance pass while still rejecting a palm that is far too small; the remaining values are
 * the vendor-recommended presets. This tunes only the *client* capture gate — DIS liveness and
 * classification remain the authoritative server-side checks.
 */
const PALM_QUALITY_THRESHOLDS: PalmThresholds = {
  confidence: { min: 0.8 },
  brightness: { min: 0.25, max: 0.9 },
  sharpness: { min: 0.07 },
  size: { min: 0.34, max: 0.8 },
  edgeDistanceToImageShorterSideRatio: { min: 0.03 },
};

export const PalmCapture = () => {
  const { t } = useTranslation();
  const { attempt, capturedHand, expectedHand, rejection, submitPalm, uiStep } = useUploadPalm();

  async function handleComplete(imageData: PalmOnCompleteCallbackImage) {
    await submitPalm(imageData.image);
  }

  // Real-time positioning guidance rendered by the companion UI overlay (mirrors the face step).
  const instructions: Partial<PalmInstructions> = {
    candidate_selection: t('steps.palmCapture.instructions.candidateSelection'),
    palm_centering: t('steps.palmCapture.instructions.palmCentering'),
    palm_not_present: t('steps.palmCapture.instructions.palmNotPresent'),
    palm_too_far: t('steps.palmCapture.instructions.palmTooFar'),
    palm_too_close: t('steps.palmCapture.instructions.palmTooClose'),
    sharpness_too_low: t('steps.palmCapture.instructions.sharpnessTooLow'),
    brightness_too_low: t('steps.palmCapture.instructions.brightnessTooLow'),
    brightness_too_high: t('steps.palmCapture.instructions.brightnessTooHigh'),
    device_pitched: t('steps.palmCapture.instructions.devicePitched'),
    template_extraction_quality_too_low: t('steps.palmCapture.instructions.qualityTooLow'),
  };

  // Hand outline placeholder: the expected hand when the BFF is forcing the second palm, otherwise
  // a left-hand outline as a neutral positioning guide for the first / single capture.
  const placeholder = expectedHand ?? 'left';

  // Remount the auto-capture element for each fresh capture: after the first palm is accepted the
  // hand changes, and after a rejection the attempt counter changes — either forces a new capture
  // session (the DOT element stops after firing `onComplete`).
  const cameraKey = `${capturedHand ?? 'first'}-${attempt}`;

  const isSecondPalm = Boolean(capturedHand);
  const heading = isSecondPalm ? t('steps.palmCapture.secondHeading') : t('steps.palmCapture.heading');
  const instruction =
    isSecondPalm && expectedHand
      ? t('steps.palmCapture.secondInstruction', { hand: t(`steps.palmCapture.hand.${expectedHand}`) })
      : t('steps.palmCapture.instruction');

  return (
    <>
      <SC.Prompt>
        <SC.PromptHeading>{heading}</SC.PromptHeading>
        <SC.PromptText>{instruction}</SC.PromptText>
        {isSecondPalm && expectedHand ? <SC.HandBadge>{t(`steps.palmCapture.hand.${expectedHand}`)}</SC.HandBadge> : null}
      </SC.Prompt>

      <GSC.RelativeContainer>
        <CaptureWrapper>
          <GSC.CaptureContainer>
            {uiStep === PalmUiStep.PROCESSING ? (
              <SC.ProcessingOverlay>
                <Loader />
                <span>{t('common.state.processing')}</span>
              </SC.ProcessingOverlay>
            ) : (
              <>
                <PalmCamera
                  key={cameraKey}
                  assetsDirectoryPath={getPublicAsset('/')}
                  camera={{ facingMode: 'user' }}
                  qualityAttributeThresholds={PALM_QUALITY_THRESHOLDS}
                  onComplete={handleComplete}
                  onError={onAutoCaptureError}
                />
                <PalmCameraUi
                  key={`ui-${cameraKey}`}
                  appStateInstructions={{ loading: { text: t('common.state.loading') }, waiting: { visible: false } }}
                  control={{ showCameraButtons: true, showDetectionLayer: true }}
                  instructions={instructions}
                  placeholder={placeholder}
                />
              </>
            )}
          </GSC.CaptureContainer>
        </CaptureWrapper>

        {rejection ? <SC.Banner $variant="error">{rejection.message}</SC.Banner> : null}
        {attempt > 0 ? (
          <SC.AttemptCounter>{t('steps.palmCapture.attempt', { current: attempt + 1, max: 5 })}</SC.AttemptCounter>
        ) : null}
      </GSC.RelativeContainer>

      <Spacer $top={1}>
        <SecuredByBadge />
      </Spacer>

      {config.photoUploadEnabled ? (
        <ManualMessageUpload
          id="palm_manual_upload"
          onComplete={(content) => {
            void handleComplete({
              image: new Blob([new Uint8Array(content)], { type: 'image/jpeg' }),
              data: {} as PalmOnCompleteCallbackImage['data'],
            });
          }}
        />
      ) : null}
    </>
  );
};
